import { useEffect, useState } from "react";
import { Search, Shield, MessageCircle, Star, ArrowRight, TreePine, Truck, CreditCard, Info } from "lucide-react";
import { useDocumentTitle } from "@/hooks/useDocumentTitle";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import ListingCard from "@/components/ListingCard";
import { supabase } from "@/integrations/supabase/client";

const Index = () => {
  useDocumentTitle(
    "Bois de chauffage à Chimay - Achat & Vente sécurisée | ChimayBois",
    "Trouvez et réservez votre bois de chauffage à Chimay, Couvin et Momignies. Paiement de l'acompte sécurisé et mise en relation directe avec les vendeurs locaux."
  );
  const [featured, setFeatured] = useState<any[]>([]);

  useEffect(() => {
    const fetchFeatured = async () => {
      const { data: listingsData } = await supabase
        .from("listings")
        .select("*")
        .eq("status", "active")
        .order("created_at", { ascending: false })
        .limit(3);

      if (listingsData && listingsData.length > 0) {
        const sellerIds = [...new Set(listingsData.map((l) => l.seller_id))];
        const [{ data: sellers }, { data: reviewsData }] = await Promise.all([
          supabase.from("seller_profiles_public").select("*").in("user_id", sellerIds),
          supabase.from("reviews").select("seller_id, rating").in("seller_id", sellerIds),
        ]);
        const sellerMap = Object.fromEntries((sellers ?? []).map((s) => [s.user_id, s]));

        // Compute average review rating per seller
        const ratingMap: Record<string, { sum: number; count: number }> = {};
        (reviewsData ?? []).forEach((r: any) => {
          if (!ratingMap[r.seller_id]) ratingMap[r.seller_id] = { sum: 0, count: 0 };
          ratingMap[r.seller_id].sum += r.rating;
          ratingMap[r.seller_id].count += 1;
        });

        setFeatured(listingsData.map((l) => {
          const seller = sellerMap[l.seller_id] || null;
          const avg = ratingMap[l.seller_id] ? ratingMap[l.seller_id].sum / ratingMap[l.seller_id].count : 0;
          const reviewCount = ratingMap[l.seller_id]?.count ?? 0;
          return { ...l, seller, rating: avg, reviewCount };
        }));
      } else {
        setFeatured([]);
      }
    };
    fetchFeatured();
  }, []);

  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />
      <main>

      {/* Beta banner */}
      <div className="bg-primary/10 border-b border-primary/20">
        <div className="container flex items-center justify-center gap-2 py-2 text-xs sm:text-sm text-primary">
          <Info className="h-4 w-4 shrink-0" />
          <span>
            ChimayBois est actuellement en <strong>phase de test (Beta)</strong>. Votre aide nous est précieuse !
            Suggestion ou problème ? Écrivez-nous à{" "}
            <a href="mailto:info@chimaybois.be" className="underline font-semibold hover:text-primary/80">info@chimaybois.be</a>
          </span>
        </div>
      </div>

      {/* Hero */}
      <section className="relative min-h-[70vh] flex items-center justify-center overflow-hidden">
        <img
          src="/images/hero-firewood.webp"
          alt="Stères de bois de chauffage empilés dans une forêt près de Chimay au coucher du soleil"
          className="absolute inset-0 w-full h-full object-cover"
          width={1920}
          height={1080}
          fetchPriority="high"
          loading="eager"
          decoding="async"
        />
        <div className="absolute inset-0 bg-gradient-hero" />
        <div className="relative z-10 container text-center text-primary-foreground py-20">
          <h1 className="font-display text-4xl md:text-6xl font-extrabold mb-4 leading-tight animate-fade-in">
            Bois de chauffage à Chimay
            <br />
            <span className="text-gradient-warm">près de chez vous</span>
          </h1>
          <p className="text-lg md:text-xl max-w-2xl mx-auto mb-8 text-primary-foreground/90 animate-fade-in" style={{ animationDelay: "0.15s" }}>
            Achetez du bois local en toute confiance. Vendeurs vérifiés, prix transparents, livraison dans la région de Chimay.
          </p>

          {/* Search bar */}
          <div className="max-w-xl mx-auto bg-background/95 backdrop-blur-sm rounded-lg p-2 flex gap-2 shadow-elevated animate-fade-in" style={{ animationDelay: "0.3s" }}>
            <div className="flex-1 flex items-center gap-2 px-3">
              <Search className="h-5 w-5 text-muted-foreground" />
              <input
                type="text"
                placeholder="Chêne, hêtre, frêne..."
                className="w-full bg-transparent text-foreground placeholder:text-muted-foreground text-sm outline-none py-2"
              />
            </div>
            <Button asChild>
              <Link to="/annonces">Rechercher</Link>
            </Button>
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="py-20 bg-background">
        <div className="container">
          <h2 className="font-display text-3xl md:text-4xl font-bold text-center mb-4">
            Comment ça marche ?
          </h2>
          <p className="text-muted-foreground text-center max-w-lg mx-auto mb-12">
            Un système sécurisé qui protège acheteurs et vendeurs.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              { icon: CreditCard, title: "Réservez votre bois", desc: "Cliquez sur « Réserver » et réglez les frais de service de 10 € via Stripe. Ce paiement sécurise votre réservation." },
              { icon: MessageCircle, title: "Chat privé & coordonnées", desc: "Une fois le paiement validé, vous débloquez l'accès au chat privé et recevez les coordonnées du vendeur pour finaliser la livraison." },
              { icon: Truck, title: "Livraison & paiement final", desc: "Organisez la livraison via le chat. Le vendeur déduit les 10 € déjà payés du montant total. Vous ne payez que le solde restant à la livraison." },
            ].map((step, i) => (
              <div
                key={step.title}
                className="text-center animate-fade-in"
                style={{ animationDelay: `${i * 0.1}s` }}
              >
                <div className="w-14 h-14 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4">
                  <step.icon className="h-6 w-6 text-primary" />
                </div>
                <div className="text-xs font-bold text-accent-foreground bg-accent/20 rounded-full inline-block px-3 py-1 mb-2">
                  Étape {i + 1}
                </div>
                <h3 className="font-display text-lg font-semibold mb-2">{step.title}</h3>
                <p className="text-sm text-muted-foreground">{step.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Featured listings */}
      <section className="py-20 bg-card">
        <div className="container">
          <div className="flex items-end justify-between mb-10">
            <div>
              <h2 className="font-display text-3xl font-bold mb-2">Annonces récentes</h2>
              <p className="text-muted-foreground">Les dernières offres de bois de chauffage près de Chimay</p>
            </div>
            <Button variant="ghost" asChild className="hidden md:inline-flex gap-1">
              <Link to="/annonces">
                Voir tout <ArrowRight className="h-4 w-4" />
              </Link>
            </Button>
          </div>

          {featured.length === 0 ? (
            <p className="text-center py-12 text-muted-foreground">Aucune annonce pour le moment. Soyez le premier à vendre !</p>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {featured.map((listing) => (
                <ListingCard key={listing.id} listing={listing} />
              ))}
            </div>
          )}

          <div className="md:hidden mt-6 text-center">
            <Button variant="outline" asChild className="gap-1">
              <Link to="/annonces">
                Voir toutes les annonces <ArrowRight className="h-4 w-4" />
              </Link>
            </Button>
          </div>
        </div>
      </section>

      {/* Trust section */}
      <section className="py-20 bg-background">
        <div className="container text-center">
          <h2 className="font-display text-3xl font-bold mb-4">Pourquoi ChimayBois ?</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mt-10">
            {[
              { icon: Shield, title: "Transactions sécurisées", desc: "Le paiement de la commission protège les deux parties et garantit le sérieux." },
              { icon: Star, title: "Vendeurs notés", desc: "Consultez les avis vérifiés pour acheter en toute confiance." },
              { icon: TreePine, title: "100% local", desc: "Du bois de la région de Chimay, livré à quelques kilomètres." },
            ].map((item) => (
              <div key={item.title} className="p-6 rounded-lg bg-card shadow-card">
                <item.icon className="h-8 w-8 text-primary mx-auto mb-4" />
                <h3 className="font-display text-lg font-semibold mb-2">{item.title}</h3>
                <p className="text-sm text-muted-foreground">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-gradient-forest text-primary-foreground">
        <div className="container text-center">
          <h2 className="font-display text-3xl font-bold mb-4">Vous avez du bois à vendre ?</h2>
          <p className="text-primary-foreground/80 max-w-md mx-auto mb-8">
            Publiez votre annonce gratuitement et touchez des acheteurs dans toute la région.
          </p>
          <Button asChild size="lg" variant="secondary" className="font-semibold">
            <Link to="/vendre">Publier une annonce gratuite</Link>
          </Button>
        </div>
      </section>

      </main>
      <Footer />
    </div>
  );
};

export default Index;
